import { useEffect, useRef, useState } from "react";
import { NavLink, Link, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import i18n from "../../locales/i18n";
// import logo from "../../assets/logos/logo.png";
import logo from "../../assets/images/misc/madina-logo-gray.webp";
import "../../styles/MainSlider.css";

/* Routes considered "active" for each dropdown parent */
const qualityRoutes = ["/quality-control", "/certifications"];

function Dropdown({
  label,
  routes,
  children,
}: {
  label: string;
  routes: string[];
  children: React.ReactNode;
}) {
  const { pathname } = useLocation();
  const isActive = routes.some((r) => pathname.startsWith(r));
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLLIElement>(null);

  // Close on outside click
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node))
        setOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  return (
    <li ref={ref} className={`nav-item dropdown${open ? " show" : ""}`}>
      <button
        type="button"
        className={`nav-link dropdown-toggle${isActive ? " nav-active" : ""}`}
        onClick={() => setOpen((o) => !o)}
        aria-expanded={open ? "true" : "false"}
      >
        {label}
      </button>
      <ul className={`dropdown-menu${open ? " show" : ""}`}>{children}</ul>
    </li>
  );
}

function DropItem({
  to,
  label,
  onClick,
}: {
  to: string;
  label: string;
  onClick: () => void;
}) {
  return (
    <li>
      <NavLink to={to} className="dropdown-item" onClick={onClick}>
        {label}
      </NavLink>
    </li>
  );
}

function MainHeader() {
  const { t } = useTranslation();
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);

  // Force re-render on language change
  const [, tick] = useState(0);
  const isArabic = i18n.language === "ar";

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    const handler = () => tick((n) => n + 1);
    i18n.on("languageChanged", handler);
    return () => i18n.off("languageChanged", handler);
  }, []);

  const close = () => setMobileOpen(false);

  return (
    <header className={`site-header${scrolled ? " scrolled" : ""}`}>
      <div className="header-inner">
        {/* ── Logo ── */}
        <Link to="/" className="header-logo-link" onClick={close}>
          <img src={logo} alt="Al Madina Steel" className="header-logo" />
        </Link>

        {/* ── Hamburger (mobile) ── */}
        <button
          type="button"
          className={`hamburger${mobileOpen ? " is-open" : ""}`}
          aria-label="Toggle navigation"
          onClick={() => setMobileOpen((o) => !o)}
        >
          <span />
          <span />
          <span />
        </button>

        {/* ── Nav ── */}
        <nav className={`header-nav${mobileOpen ? " nav-open" : ""}`}>
          <ul className="nav-list">
            <li className="nav-item">
              <NavLink
                to="/"
                end
                className={({ isActive }) =>
                  `nav-link${isActive ? " nav-active" : ""}`
                }
                onClick={close}
              >
                {t("Home")}
              </NavLink>
            </li>

            <li className="nav-item">
              <NavLink
                to="/about"
                className={({ isActive }) =>
                  `nav-link${isActive ? " nav-active" : ""}`
                }
                onClick={close}
              >
                {t("About Us")}
              </NavLink>
            </li>

            <li className="nav-item">
              <NavLink
                to="/projects"
                className={({ isActive }) =>
                  `nav-link${isActive ? " nav-active" : ""}`
                }
                onClick={close}
              >
                {t("Projects")}
              </NavLink>
            </li>

            <li className="nav-item">
              <NavLink
                to="/products"
                className={({ isActive }) =>
                  `nav-link${isActive ? " nav-active" : ""}`
                }
                onClick={close}
              >
                {t("Products")}
              </NavLink>
            </li>
            <li>
              <NavLink
                to="/quality-control"
                className={({ isActive }) =>
                  `nav-link${isActive ? " nav-active" : ""}`
                }
                onClick={close}
              >
                {t("Quality")}
              </NavLink>
            </li>
            <li className="nav-item">
              <NavLink
                to="/faqs"
                className={({ isActive }) =>
                  `nav-link${isActive ? " nav-active" : ""}`
                }
                onClick={close}
              >
                {t("FAQs")}
              </NavLink>
            </li>

            <li className="nav-item">
              <NavLink
                to="/contact-us"
                className={({ isActive }) =>
                  `nav-link${isActive ? " nav-active" : ""}`
                }
                onClick={close}
              >
                {t("Contact Us")}
              </NavLink>
            </li>
          </ul>

          {/* ── Language switcher ── */}
          <div className="lang-switcher">
            {!isArabic ? (
              <button
                type="button"
                className="lang-btn"
                onClick={() => {
                  i18n.changeLanguage("ar");
                  close();
                }}
              >
                AR
              </button>
            ) : (
              <button
                type="button"
                className="lang-btn"
                onClick={() => {
                  i18n.changeLanguage("en");
                  close();
                }}
              >
                EN
              </button>
            )}
          </div>
        </nav>
      </div>
    </header>
  );
}

export default MainHeader;
